/**
 * Caption cleaning + graphic-copy demarcation (client mirror of
 * backend/services/caption_cleaner.py). Used for the instant "Copy clean
 * caption" button and to derive a best-effort headline/sub-header split for
 * legacy cards that pre-date the demarcated fields.
 */

const DASHES = ["\u2014", "\u2013", "\u2015", "\u2012", "\u2212"]; // em, en, bar, figure, minus

export function cleanCaption(text) {
  if (!text) return "";
  let t = String(text);

  // Dashes → comma + space.
  for (const ch of DASHES) {
    t = t.split(` ${ch} `).join(", ");
    t = t.split(`${ch} `).join(", ");
    t = t.split(` ${ch}`).join(", ");
    t = t.split(ch).join(", ");
  }

  // Markdown emphasis.
  t = t.replace(/\*\*([\s\S]+?)\*\*/g, "$1");
  t = t.replace(/__([\s\S]+?)__/g, "$1");
  t = t.replace(/\*([\s\S]+?)\*/g, "$1");
  t = t.replace(/(^|\W)_([\s\S]+?)_(\W|$)/g, "$1$2$3");
  t = t.replace(/`/g, "");

  // Markdown links [text](url) → text
  t = t.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  // Headers require a trailing space (so #hashtags survive); blockquotes.
  t = t.replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "");
  t = t.replace(/^[ \t]{0,3}>[ \t]?/gm, "");

  // Tidy artefacts.
  t = t.replace(/,\s*,+/g, ",");
  t = t.replace(/\s+,/g, ",");
  t = t.replace(/[ \t]{2,}/g, " ");
  t = t.replace(/[ \t]+$/gm, "");
  t = t.replace(/\n{3,}/g, "\n\n");

  return t.trim();
}

/**
 * Best-effort legacy split → { headline, subheader, caption }.
 * First non-empty line = headline; next non-empty line (if <=120 chars) =
 * sub-header; the rest = caption.
 */
export function splitBody(bodyMd) {
  if (!bodyMd || !bodyMd.trim()) return { headline: "", subheader: "", caption: "" };
  const rawLines = bodyMd.split("\n");
  const idxs = [];
  rawLines.forEach((ln, i) => { if (ln.trim()) idxs.push(i); });
  if (idxs.length === 0) return { headline: "", subheader: "", caption: "" };

  const headline = cleanCaption(rawLines[idxs[0]].trim());
  let subheader = "";
  let start = idxs[0] + 1;
  if (idxs.length >= 2) {
    const cand = rawLines[idxs[1]].trim();
    if (cand.length <= 120) { subheader = cleanCaption(cand); start = idxs[1] + 1; }
  }
  const caption = cleanCaption(rawLines.slice(start).join("\n").trim());
  return { headline, subheader, caption };
}

/**
 * Resolve a card's display parts. If the card carries explicit graphic fields
 * (new cards), the caption is the (cleaned) body. Otherwise fall back to a
 * best-effort split of the body (legacy cards).
 */
export function deriveCaptionParts(card) {
  if (!card) return { headline: "", subheader: "", caption: "", legacy: false };
  const hasExplicit = (card.graphic_headline && card.graphic_headline.trim())
    || (card.graphic_subheader && card.graphic_subheader.trim());
  if (hasExplicit) {
    return {
      headline: card.graphic_headline || "",
      subheader: card.graphic_subheader || "",
      caption: cleanCaption(card.body_md || ""),
      legacy: false,
    };
  }
  return { ...splitBody(card.body_md || ""), legacy: true };
}
